import { PropsWithChildren, ReactElement } from "react";
import styles from "./UploadCard.module.less";
import { setTrackingId } from "client/Misc/utils";
import classNames from "classnames";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { localize } from "client_legacy/ndaw/culture";
import { useCallbackWithoutArguments } from "client/Hooks";

export type UploadCardStatusType =
	| "failed"
	| "success";

export type UploadCardProps = {
	status?: UploadCardStatusType;
	trackingId: string;
	onClick?: () => void;
};

export const UploadCard = (props: PropsWithChildren<UploadCardProps>): ReactElement => {
	const {
		status,
		children,
		trackingId,
		onClick: onClickFromProps
	} = props;

	const onClick: typeof onClickFromProps = useCallbackWithoutArguments(onClickFromProps);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_succeed]: status === "success",
					[styles.root_failed]: status === "failed",
					[styles.root_hoverable]: onClick
				}
			)}
			onClick={onClick}
		>
			{children}
		</div>
	);
};
UploadCard.displayName = "UploadCard";

const UploadCardThumbnail = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.thumbnail}>
			{children}
		</div>
	);
};
UploadCardThumbnail.displayName = "UploadCardThumbnail";

const UploadCardContent = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.content}>
			{children}
		</div>
	);
};
UploadCardContent.displayName = "UploadCardContent";

type UploadCardProgressBarProps = {
	progress: number;
};

const UploadCardProgressBar = (props: UploadCardProgressBarProps): ReactElement => {
	const {
		progress
	} = props;

	return (
		<div className={styles.progressbar}>
			<ProgressBar
				value={progress}
				trackingId="progress-bar"
				ariaLabel={localize("_UploadProgress")}
			/>
		</div>
	);
};
UploadCardProgressBar.displayName = "UploadCardProgressBar";

const UploadCardActions = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.actions}>
			{children}
		</div>
	);
};
UploadCardActions.displayName = "UploadCardActions";

const UploadCardStatus = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.status}
		>
			{children}
		</div>
	);
};
UploadCardStatus.displayName = "UploadCardStatus";

const UploadCardTitle = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<h4
			className={styles.title}
		>
			{children}
		</h4>
	);
};
UploadCardTitle.displayName = "UploadCardTitle";

UploadCard.Thumbnail = UploadCardThumbnail;
UploadCard.Content = UploadCardContent;
UploadCard.ProgressBar = UploadCardProgressBar;
UploadCard.Actions = UploadCardActions;
UploadCard.Status = UploadCardStatus;
UploadCard.Title = UploadCardTitle;
